import { Box, Grid } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import DashboardSkeleton from "./DashboardSkeleton";
import styles from "./Dashboard.module.scss";
import ProfitByUserTable from "./Tables/ProfitByUserTable";
import AllResultTable from "./Tables/AllResultTable";
import { useGetUsersQuery } from "../../services/user";
import {
	useGetDashboardBranchDataQuery,
	useGetDashboardDataQuery,
	useLazyGetUserLoadByMonthUserQuery,
} from "../../services/notifications";
import OrganizationBigTable from "./Tables/OrganizationBigTable";
import StatTile from "./StatTile";
import { AvgPerLoadChart, LoadVolumeChart, ProfitTrendChart } from "./DashboardCharts";
import {
	buildDemoBranchData,
	buildDemoMonthlyRaw,
	buildDemoUsers,
	buildSummary,
	formatCurrency,
	formatNumber,
	resolveSeries,
} from "./dashboardMetrics";

// How long the skeleton stays up before the dashboard is revealed.
const INTRO_MS = 3000;

const SectionHead = ({ title }) => (
	<div className={styles.sectionHead}>
		<h2 className={styles.sectionTitle}>{title}</h2>
		<span className={styles.sectionRule} />
	</div>
);

const ChartCard = ({ title, subtitle, hasData, emptyMessage, children }) => (
	<div className={styles.chartCard}>
		<div className={styles.chartHead}>
			<h3 className={styles.chartTitle}>{title}</h3>
			<span className={styles.chartSubtitle}>{subtitle}</span>
		</div>
		{hasData ? children : <div className={styles.emptyState}>{emptyMessage}</div>}
	</div>
);

const Dashboard = () => {
	const { data: users } = useGetUsersQuery();
	const { data: dashboardData } = useGetDashboardDataQuery();
	const { data: dashboardBranchData } = useGetDashboardBranchDataQuery();
	const [triggerUserMonth, { data: monthlyData }] = useLazyGetUserLoadByMonthUserQuery();

	useEffect(() => {
		triggerUserMonth();
	}, []);

	/*
	 * Hold the skeleton for a fixed interval, then reveal everything at once.
	 *
	 * The requests still fire immediately on mount and resolve in parallel with
	 * this timer — the delay does not postpone them. What it buys is a single
	 * clean transition instead of five sections popping in at different moments
	 * as four independent queries settle.
	 *
	 * Cleared on unmount so a fast navigation away cannot set state on an
	 * unmounted component.
	 */
	const [introDone, setIntroDone] = useState(false);
	useEffect(() => {
		const timer = setTimeout(() => setIntroDone(true), INTRO_MS);
		return () => clearTimeout(timer);
	}, []);

	// Everything analytical on this page is derived from the monthly aggregate.
	// The /dashboard endpoint only returns today's load count and today's profit.
	// resolveSeries returns the sample dataset whenever there is no real activity,
	// INCLUDING while requests are still in flight, so the page paints instantly
	// rather than waiting on four round trips. Real data replaces it on arrival.
	const { series, today, isDemo } = useMemo(
		() => resolveSeries(monthlyData, dashboardData),
		[monthlyData, dashboardData]
	);
	const summary = useMemo(() => buildSummary(series, today), [series, today]);

	// The tables consume the raw API shapes, so they get their own fallbacks
	// built to match. Same rule as the cards: real data always wins.
	const tableMonthly = useMemo(
		() => (isDemo ? buildDemoMonthlyRaw() : monthlyData),
		[isDemo, monthlyData]
	);

	// The branch endpoint returns real members even with no loads, so "empty"
	// here means every member has zero activity this month, not zero members.
	const branchHasActivity = useMemo(() => {
		const branches = dashboardBranchData?.branches;
		if (!Array.isArray(branches)) return false;
		return branches.some((b) => (b?.members || []).some((m) => (m?.loadsMonth || []).length > 0));
	}, [dashboardBranchData]);

	const tableBranchData = useMemo(
		() => (branchHasActivity ? dashboardBranchData : buildDemoBranchData()),
		[branchHasActivity, dashboardBranchData]
	);

	// Memoised because it was being called inline in JSX, allocating a fresh
	// twelve-row array on every render and handing ProfitByUserTable a new prop
	// identity each time, which forced it to re-render for no reason.
	const userTableFallback = useMemo(() => buildDemoMonthlyRaw(), []);
	const usersFallback = useMemo(() => buildDemoUsers(), []);

	// The sample fallback guarantees a populated series, so the charts always
	// have something to draw.
	const hasYearData = summary.ytdLoads > 0 || summary.ytdProfit !== 0;

	/*
	 * The gate is the intro timer, NOT any query's loading flag.
	 *
	 * Blocking on isLoading meant the page showed nothing until the slowest of
	 * four requests returned, and a failed request left it blank indefinitely.
	 * Each child is handed isLoading={false} and a fallback dataset, so every
	 * section renders complete content the moment the skeleton clears —
	 * populated with real figures where they exist and samples where they do not.
	 *
	 * Safe only because the children tolerate undefined props: ProfitByUserTable
	 * used to do `users.users` and crashed the whole page whenever that query was
	 * pending or had failed.
	 */
	if (!introDone) {
		return <DashboardSkeleton />;
	}

	const todayLabel = new Date().toLocaleDateString("en-US", {
		weekday: "long",
		month: "long",
		day: "numeric",
		year: "numeric",
	});

	return (
		<div className={styles.page}>
			<div className={styles.pageHead}>
				<h1 className={styles.pageTitle}>Operations Overview</h1>
				<span className={styles.pageMeta}>{todayLabel}</span>
			</div>

			<SectionHead title="Today" />
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} md={4} lg={2.4}>
					<StatTile
						label="Loads Today"
						value={formatNumber(summary.loadsToday)}
						sublabel="created since midnight"
						tone="blue"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={4} lg={2.4}>
					<StatTile
						label="Profit Today"
						value={formatCurrency(summary.profitToday)}
						sublabel="booked today"
						tone="blue"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={4} lg={2.4}>
					<StatTile
						label="This Month"
						value={formatCurrency(summary.thisMonthProfit, { compact: true })}
						sublabel={`${formatNumber(summary.thisMonthLoads)} loads`}
						delta={summary.momChange}
						tone="orange"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={4} lg={2.4}>
					<StatTile
						label="Avg / Load (YTD)"
						value={formatCurrency(summary.avgPerLoad)}
						sublabel="profit per load"
						tone="blue"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={4} lg={2.4}>
					<StatTile
						label="Best Month"
						value={
							summary.bestMonthLabel
								? formatCurrency(summary.bestMonthProfit, { compact: true })
								: "—"
						}
						sublabel={summary.bestMonthLabel || "no activity yet"}
						tone="blue"
					/>
				</Grid>
			</Grid>

			<SectionHead title={`Year to date · ${new Date().getFullYear()}`} />
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} md={3}>
					<StatTile
						label="YTD Profit"
						value={formatCurrency(summary.ytdProfit, { compact: true })}
						sublabel={`across ${summary.activeMonths} active month${
							summary.activeMonths === 1 ? "" : "s"
						}`}
						tone="navy"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatTile
						label="YTD Loads"
						value={formatNumber(summary.ytdLoads)}
						sublabel="delivered this year"
						tone="navy"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatTile
						label="Avg / Active Month"
						value={formatCurrency(summary.avgPerMonth, { compact: true })}
						sublabel="mean monthly profit"
						tone="navy"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatTile
						label="Last Month"
						value={
							summary.lastMonthProfit === null
								? "—"
								: formatCurrency(summary.lastMonthProfit, { compact: true })
						}
						sublabel="previous month profit"
						tone="navy"
					/>
				</Grid>
			</Grid>

			<SectionHead title="Trends" />
			<Grid container spacing={2}>
				<Grid item xs={12} lg={8}>
					<ChartCard
						title="Monthly Profit"
						subtitle="Total profit booked per month this year"
						hasData={hasYearData}
						emptyMessage="No loads recorded this year yet. The trend appears once loads are created."
					>
						<ProfitTrendChart series={series} />
					</ChartCard>
				</Grid>
				<Grid item xs={12} lg={4}>
					<ChartCard
						title="Profit per Load"
						subtitle="Efficiency — profit divided by load count"
						hasData={hasYearData}
						emptyMessage="Needs at least one load to calculate."
					>
						<AvgPerLoadChart series={series} />
					</ChartCard>
				</Grid>
				<Grid item xs={12}>
					<ChartCard
						title="Load Volume"
						subtitle="Loads created per month · busiest month highlighted"
						hasData={hasYearData}
						emptyMessage="No loads recorded this year yet."
					>
						<LoadVolumeChart series={series} />
					</ChartCard>
				</Grid>
			</Grid>

			<SectionHead title="Team performance" />
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<div className={styles.panel}>
						<ProfitByUserTable
							users={users}
							isLoading={false}
							fallbackMonthly={userTableFallback}
							fallbackUsers={usersFallback}
						/>
					</div>
				</Grid>
				<Grid item xs={12} md={6}>
					<div className={styles.panel}>
						<AllResultTable monthlyData={tableMonthly} isLoading={false} />
					</div>
				</Grid>
			</Grid>

			<SectionHead title="Branch breakdown" />
			<Box className={styles.panel} sx={{ overflowX: "auto" }}>
				<OrganizationBigTable dashboardBigTableData={tableBranchData} isLoading={false} />
			</Box>
		</div>
	);
};

export default Dashboard;
